import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Loader2, Search, Sparkles, X, Package } from 'lucide-react';
import ProductForm, { emptyProduct } from './ProductForm';

/**
 * ProductPickerOrCreate — controlled input that lets the user either pick an
 * existing product OR create a brand-new one inline. Emits a normalised
 * `{product_id?, new_product?, product_name, unit_cost, unit_price}` object.
 *
 * Two output shapes:
 *   1. Existing product picked:
 *        onChange({product_id, product_name, unit_cost, unit_price})
 *   2. New product typed + confirmed:
 *        onChange({product_name, unit_cost, unit_price,
 *                  new_product: {name, category, unit_price, unit_cost, min_stock_level}})
 *
 * `value` is the current object (may be empty).
 */
export default function ProductPickerOrCreate({
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  placeholder = 'Type a product name…',
  testid = 'product-picker',
}) {
  const [query, setQuery] = useState(value?.product_name || '');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newForm, setNewForm] = useState(null);   // ← inline create panel when non-null
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setMatches([]); return; }
    setLoading(true);
    try {
      // Existing /products endpoint accepts `search` query.
      const res = await api.get('/products', { params: { search: q, limit: 12 } });
      setMatches(res.data?.products || res.data || []);
    } catch {
      setMatches([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, search]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pickExisting = (p) => {
    onChange({
      product_id: p.product_id,
      product_name: p.name,
      unit_cost: Number(p.unit_cost || 0),
      unit_price: Number(p.unit_price || 0),
    });
    setQuery(p.name);
    setOpen(false);
    setNewForm(null);
  };

  const startCreateNew = () => {
    const name = query.trim();
    if (!name) return;
    // Prefill the full-form dialog with the typed name and sensible defaults.
    setNewForm({ ...emptyProduct, name, stock_quantity: 0 });
    setOpen(false);
  };

  const confirmCreateNew = (payload) => {
    const name = (payload?.name || '').trim();
    if (!name) {
      toast.error('Product name is required');
      return;
    }
    const cost = Number(payload.unit_cost || 0);
    const price = Number(payload.unit_price || 0);
    onChange({
      // No product_id — the backend will create the product on submit.
      product_name: name,
      unit_cost: cost,
      unit_price: price,
      new_product: {
        name,
        generic_name: payload.generic_name || '',
        category: payload.category || 'General',
        manufacturer: payload.manufacturer || '',
        unit_price: price,
        unit_cost: cost,
        stock_quantity: 0,  // stock lands via the PO receive, not the picker
        min_stock_level: Number(payload.min_stock_level || 10),
        requires_prescription: !!payload.requires_prescription,
        is_controlled: !!payload.is_controlled,
        description: payload.description || '',
        barcode: payload.barcode || '',
      },
    });
    setQuery(name);
    setNewForm(null);
  };

  const clearSelection = () => {
    onChange({});
    setQuery('');
    setMatches([]);
    setNewForm(null);
  };

  const hasSelection = !!(value?.product_id || value?.new_product);
  const isNewSelected = !!value?.new_product;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
          className="pl-9 pr-9"
          data-testid={testid}
        />
        {hasSelection && (
          <button type="button" onClick={clearSelection} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {hasSelection && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          {isNewSelected ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 gap-1">
              <Sparkles className="h-3 w-3" /> New: {value.product_name}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300 gap-1">
              <Package className="h-3 w-3" /> {value.product_name}
            </Badge>
          )}
        </div>
      )}
      {open && (query.trim().length >= 2 || matches.length > 0) && (
        <div className="absolute z-40 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
          {loading && (
            <div className="p-3 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Searching…</div>
          )}
          {!loading && matches.map(p => (
            <button
              key={p.product_id}
              type="button"
              onClick={() => pickExisting(p)}
              className="w-full text-left px-3 py-2 hover:bg-muted flex items-start justify-between gap-3 border-b last:border-b-0"
              data-testid={`${testid}-result-${p.product_id}`}
            >
              <div>
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {p.category || 'General'} · stock {p.stock_quantity ?? 0} · cost ₵{Number(p.unit_cost || 0).toFixed(2)}
                </div>
              </div>
              <Badge variant="secondary" className="text-[10px]">Select</Badge>
            </button>
          ))}
          {!loading && query.trim().length >= 2 && (
            <button
              type="button"
              onClick={startCreateNew}
              className="w-full text-left px-3 py-2 bg-emerald-50 hover:bg-emerald-100 border-t border-emerald-200 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50 flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200 font-medium"
              data-testid={`${testid}-create-btn`}
            >
              <Sparkles className="h-3 w-3" />
              Create new product &ldquo;{query.trim()}&rdquo;
            </button>
          )}
        </div>
      )}

      {/* Full "Add Product" dialog — same form as the Inventory page */}
      <Dialog open={!!newForm} onOpenChange={(o) => { if (!o) setNewForm(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid={`${testid}-create-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-600" /> Add new product to inventory
            </DialogTitle>
            <DialogDescription>
              This item will be created in your catalogue when you submit the purchase.
            </DialogDescription>
          </DialogHeader>
          {newForm && (
            <ProductForm
              value={newForm}
              onChange={setNewForm}
              onSubmit={confirmCreateNew}
              onCancel={() => setNewForm(null)}
              submitLabel="Use this product"
              showStock={false}   // stock arrives via the purchase receive step
              testidPrefix={`${testid}-new`}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
